import React, { useEffect, useRef, useState } from 'react';
import { Plus, ListTodo, Inbox, Send as SendIcon, Car, Paperclip, FileText, Trash2, CheckCircle2, Flag, MessageSquare, Loader2 } from 'lucide-react';
import api, { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors';

const STATUS = {
  OPEN: { label: 'Aguardando aceite', cls: 'text-amber-400 bg-amber-400/10' },
  ACCEPTED: { label: 'Em andamento', cls: 'text-blue-400 bg-blue-400/10' },
  DONE: { label: 'Concluída', cls: 'text-emerald-400 bg-emerald-400/10' },
};
const PRIO = { 'Alta': 'text-red-400 bg-red-400/10', 'Normal': 'text-muted-foreground bg-muted/40', 'Baixa': 'text-sky-400 bg-sky-400/10' };

const fmtDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const NewTaskDialog = ({ open, onClose, onDone }) => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vq, setVq] = useState('');
  const [form, setForm] = useState({ title: '', description: '', priority: 'Normal', assignee_id: '', vehicle_ids: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm({ title: '', description: '', priority: 'Normal', assignee_id: user?.user_id || '', vehicle_ids: [] });
      setVq('');
      api.get('/users').then((r) => setUsers(r.data)).catch(() => {});
      api.get('/vehicles').then((r) => setVehicles(r.data)).catch(() => {});
    }
  }, [open, user]);
  const create = async () => {
    if (!form.title.trim()) { toast({ title: 'Informe o título' }); return; }
    setSaving(true);
    try {
      await api.post('/tasks', form);
      toast({ title: 'Tarefa criada', description: form.assignee_id !== user?.user_id ? 'O responsável foi notificado e precisa aceitar.' : '' });
      onClose(); onDone();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };
  const filteredV = vehicles.filter((v) => !vq || `${v.ano} ${v.marca} ${v.modelo} ${v.placa}`.toLowerCase().includes(vq.toLowerCase())).slice(0, 8);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Nova Tarefa</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Título *</label><input data-testid="task-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Descrição</label><textarea data-testid="task-desc-input" rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Responsável *</label>
              <select data-testid="task-assignee-select" className={inputCls} value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
                {users.map((u) => <option key={u.user_id} value={u.user_id} className="bg-popover">{u.name}{u.user_id === user?.user_id ? ' (eu)' : ''} · {u.role}</option>)}
              </select>
            </div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Prioridade</label>
              <select data-testid="task-priority-select" className={inputCls} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {['Baixa', 'Normal', 'Alta'].map((p) => <option key={p} className="bg-popover">{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Linkar veículos do estoque (opcional)</label>
            <input data-testid="task-vehicle-search" className={inputCls} placeholder="Buscar veículo por marca, modelo, stock #..." value={vq} onChange={(e) => setVq(e.target.value)} />
            {vq && (
              <div className="mt-1.5 rounded-lg border border-border bg-popover max-h-[160px] overflow-y-auto">
                {filteredV.map((v) => (
                  <button key={v.id} data-testid={`task-pick-vehicle-${v.id}`} onClick={() => { if (!form.vehicle_ids.includes(v.id)) setForm({ ...form, vehicle_ids: [...form.vehicle_ids, v.id] }); setVq(''); }} className="w-full text-left text-[13px] px-3 py-2 hover:bg-muted/50">
                    {v.ano} {v.marca} {v.modelo} · #{v.placa}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {form.vehicle_ids.map((vid) => {
                const v = vehicles.find((x) => x.id === vid);
                return (
                  <span key={vid} className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-primary/15 text-primary px-2 py-1 rounded">
                    <Car className="w-3 h-3" /> {v ? `${v.ano} ${v.marca} ${v.modelo}` : vid}
                    <button onClick={() => setForm({ ...form, vehicle_ids: form.vehicle_ids.filter((x) => x !== vid) })} className="hover:text-foreground">×</button>
                  </span>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground">Cancelar</button>
          <button data-testid="task-create-btn" onClick={create} disabled={saving || !form.title.trim()} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 disabled:opacity-50">{saving ? 'Criando...' : 'Criar Tarefa'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TaskDetailDialog = ({ task, open, onClose, onChanged }) => {
  const { user } = useAuth();
  const [t, setT] = useState(task);
  const [comment, setComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { setT(task); }, [task]);
  if (!t) return null;

  const refresh = async () => {
    try { const r = await api.get('/tasks'); const nt = r.data.find((x) => x.id === t.id); if (nt) setT(nt); onChanged(); } catch (e) {}
  };
  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast({ title: okMsg }); refresh(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };
  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        await api.post(`/tasks/${t.id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast({ title: 'Anexo enviado' }); refresh();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setUploading(false); }
  };

  const isAssignee = t.assignee_id === user?.user_id;
  const st = STATUS[t.status] || STATUS.OPEN;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-lg uppercase flex items-center gap-2 flex-wrap" data-testid="task-detail-title">
            {t.title}
            <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${st.cls}`}>{st.label}</span>
            <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${PRIO[t.priority] || PRIO.Normal}`}>{t.priority}</span>
          </DialogTitle>
        </DialogHeader>
        <p className="text-[12px] text-muted-foreground -mt-2">{t.id} · Criada por <span className="text-foreground/75">{t.created_by_name}</span> para <span className="text-foreground/75">{t.assignee_name}</span> · {fmtDT(t.created_at)}</p>
        {t.description && <p className="text-sm text-foreground/75 bg-muted/40 border border-border/70 rounded-lg px-3.5 py-2.5 whitespace-pre-wrap">{t.description}</p>}

        {t.status === 'OPEN' && isAssignee && (
          <button data-testid="task-accept-btn" onClick={() => act(() => api.post(`/tasks/${t.id}/accept`), '✓ Tarefa aceita — quem criou foi avisado')} className="w-full py-3 rounded-lg text-sm font-black uppercase bg-primary hover:bg-primary/90 flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4" /> Aceitar Tarefa</button>
        )}
        {t.status === 'OPEN' && !isAssignee && <p className="text-[12px] text-amber-400 font-semibold">⏳ Aguardando {t.assignee_name} aceitar a tarefa. Você será avisado.</p>}
        {t.status === 'ACCEPTED' && t.accepted_at && <p className="text-[12px] text-emerald-400">✓ Aceita em {fmtDT(t.accepted_at)}</p>}
        {t.status === 'ACCEPTED' && (
          <button data-testid="task-complete-btn" onClick={() => act(() => api.post(`/tasks/${t.id}/complete`), '🏁 Tarefa concluída')} className="w-full py-2.5 rounded-lg text-sm font-bold uppercase bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center gap-2"><Flag className="w-4 h-4" /> Marcar como Concluída</button>
        )}
        {t.status === 'DONE' && <p className="text-[12px] text-emerald-400 font-semibold">🏁 Concluída em {fmtDT(t.done_at)}</p>}

        {(t.vehicles || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {t.vehicles.map((v) => <span key={v.id} className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-primary/15 text-primary px-2 py-1 rounded"><Car className="w-3 h-3" /> {v.desc}</span>)}
          </div>
        )}

        <div className="rounded-lg border border-border/70 p-3.5 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" /> Anexos</p>
            <button data-testid="task-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="text-[11px] px-3 py-1.5 rounded-md font-semibold bg-muted/50 border border-border hover:bg-muted/80 flex items-center gap-1.5">{uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />} Anexar</button>
          </div>
          <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" multiple className="hidden" onChange={(e) => { upload(Array.from(e.target.files)); e.target.value = ''; }} />
          {(t.files || []).map((f) => (
            <div key={f.id} className="flex items-center gap-2 text-[12px] bg-muted/40 border border-border/70 rounded px-2.5 py-1.5" data-testid={`task-file-${f.id}`}>
              <FileText className="w-3.5 h-3.5 text-primary shrink-0" />
              <a href={`${API}/files/${f.path}`} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate flex-1">{f.original_filename}</a>
              <span className="text-muted-foreground/60 text-[10px]">{f.uploaded_by}</span>
              <button data-testid={`task-file-del-${f.id}`} onClick={() => act(() => api.delete(`/tasks/${t.id}/files/${f.id}`))} className="text-red-400/70 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {(t.files || []).length === 0 && <p className="text-[11px] text-muted-foreground/60">Nenhum anexo.</p>}
        </div>

        <div className="rounded-lg border border-border/70 p-3.5 space-y-2.5">
          <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Comentários</p>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {(t.comments || []).map((c) => (
              <div key={c.id} className="text-[12px] bg-muted/40 border border-border/70 rounded px-2.5 py-2">
                <p className="font-semibold text-primary text-[11px]">{c.user_name} <span className="text-muted-foreground/60 font-normal">· {fmtDT(c.at)}</span></p>
                <p className="text-foreground/80 mt-0.5">{c.text}</p>
              </div>
            ))}
            {(t.comments || []).length === 0 && <p className="text-[11px] text-muted-foreground/60">Nenhum comentário.</p>}
          </div>
          <div className="flex gap-2">
            <input data-testid="task-comment-input" className={inputCls} placeholder="Escrever comentário..." value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && comment.trim()) { act(() => api.post(`/tasks/${t.id}/comments`, { text: comment.trim() })); setComment(''); } }} />
            <button data-testid="task-comment-send" onClick={() => { if (comment.trim()) { act(() => api.post(`/tasks/${t.id}/comments`, { text: comment.trim() })); setComment(''); } }} disabled={!comment.trim()} className="px-4 rounded-lg bg-primary hover:bg-primary/90 disabled:opacity-40"><SendIcon className="w-4 h-4" /></button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TaskCard = ({ t, onOpen }) => {
  const st = STATUS[t.status] || STATUS.OPEN;
  return (
    <button data-testid={`task-card-${t.id}`} onClick={() => onOpen(t)} className="w-full text-left rounded-lg border border-border/70 bg-popover p-3.5 hover:border-primary/50 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-sm truncate">{t.title}</p>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap ${st.cls}`}>{st.label}</span>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">{t.created_by_name} → {t.assignee_name} · {fmtDT(t.created_at)}</p>
      <div className="flex items-center gap-2 mt-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${PRIO[t.priority] || PRIO.Normal}`}>{t.priority}</span>
        {(t.vehicles || []).length > 0 && <span className="text-[10px] text-primary flex items-center gap-1"><Car className="w-3 h-3" /> {t.vehicles.length}</span>}
        {(t.comments || []).length > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {t.comments.length}</span>}
        {(t.files || []).length > 0 && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Paperclip className="w-3 h-3" /> {t.files.length}</span>}
      </div>
    </button>
  );
};

const Tasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState('mine');
  const [newOpen, setNewOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => { try { const r = await api.get('/tasks'); setTasks(r.data); } catch (e) {} };
  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);

  const mine = tasks.filter((t) => t.assignee_id === user?.user_id);
  const created = tasks.filter((t) => t.created_by_id === user?.user_id && t.assignee_id !== user?.user_id);
  const shown = tab === 'mine' ? mine : created;
  const pendingAccept = mine.filter((t) => t.status === 'OPEN').length;

  return (
    <div className="space-y-5" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 bg-muted/40 border border-border rounded-lg p-1">
          <button data-testid="tasks-tab-mine" onClick={() => setTab('mine')} className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-colors ${tab === 'mine' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}>
            <Inbox className="w-4 h-4" /> Para mim ({mine.length}){pendingAccept > 0 && <span className="bg-amber-400 text-black text-[10px] font-black rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center">{pendingAccept}</span>}
          </button>
          <button data-testid="tasks-tab-created" onClick={() => setTab('created')} className={`flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-semibold transition-colors ${tab === 'created' ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}>
            <SendIcon className="w-4 h-4" /> Designadas por mim ({created.length})
          </button>
        </div>
        <button data-testid="new-task-btn" onClick={() => setNewOpen(true)} className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Nova Tarefa</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[['OPEN', 'Aguardando aceite'], ['ACCEPTED', 'Em andamento'], ['DONE', 'Concluídas']].map(([s, label]) => {
          const col = shown.filter((t) => t.status === s);
          return (
            <div key={s} className="rounded-xl border border-border/70 bg-muted/25 p-3 min-h-[200px]" data-testid={`tasks-col-${s}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${STATUS[s].cls}`}>{label}</span>
                <span className="text-xs text-muted-foreground">{col.length}</span>
              </div>
              <div className="space-y-2.5">
                {col.map((t) => <TaskCard key={t.id} t={t} onOpen={setDetail} />)}
                {col.length === 0 && <p className="text-[12px] text-muted-foreground/50 text-center py-6">Vazio</p>}
              </div>
            </div>
          );
        })}
      </div>

      {shown.length === 0 && (
        <div className="text-center py-10 text-muted-foreground/70 flex flex-col items-center gap-2"><ListTodo className="w-10 h-10" /><p className="text-sm">Nenhuma tarefa {tab === 'mine' ? 'atribuída a você' : 'designada por você'} ainda.</p></div>
      )}

      <NewTaskDialog open={newOpen} onClose={() => setNewOpen(false)} onDone={load} />
      <TaskDetailDialog task={detail} open={!!detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
};

export default Tasks;
